import { Link } from "react-router-dom";
import { COMPANY, NAV_LINKS, IMAGES } from "../constants/config";
import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";
import SectionLink from "./SectionLink";
import { scrollToTop } from "../utils/sectionNav";
import BrandLogo from "./BrandLogo";

const LEGAL_LINKS = [
  {
    label: "Vulnerability Disclosure Policy",
    to: "/vulnerability-disclosure-policy",
  },
  { label: "Terms and Conditions", to: "/terms-and-conditions" },
];

function FooterNav() {
  return (
    <nav aria-label="Footer navigation">
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-[#5d69be] sm:text-base">
        Quick Links
      </h3>
      <ul className="flex flex-col">
        {NAV_LINKS.map((link) => (
          <li key={link.sectionId}>
            <SectionLink
              sectionId={link.sectionId}
              className="inline-flex min-h-[32px] items-center text-sm text-[#3d428a] transition-colors hover:text-[#2d3270] sm:text-base"
            >
              {link.label}
            </SectionLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function FooterContact() {
  const corporateAddress = COMPANY.corporateAddress || COMPANY.address;
  const directionsUrl = "https://maps.app.goo.gl/wCzoNcqQrKRDJa6r6";

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-[#5d69be] sm:text-base">
        Contact
      </h3>
      <ul className="flex flex-col gap-2 text-sm text-[#3d428a] sm:text-base">
        <li>
          <a
            href={`tel:${COMPANY.phone}`}
            className="transition-colors hover:text-[#2d3270]"
          >
            {COMPANY.phone}
          </a>
        </li>
        <li>
          <a
            href={`mailto:${COMPANY.email}`}
            className="break-all transition-colors hover:text-[#2d3270]"
          >
            {COMPANY.email}
          </a>
        </li>
        <li>
          <address className="grid grid-cols-1 gap-4 not-italic leading-relaxed text-[#3d428a]/90 sm:grid-cols-2">
            <span>
              <span className="block font-bold text-[#3d428a]">
                Corporate Address
              </span>
              <span className="mt-1 block">{corporateAddress}</span>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#5d69be]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#5d69be] transition-colors hover:border-[#5d69be] hover:bg-[#5d69be] hover:text-white sm:text-sm"
                aria-label="Get directions to our corporate office (opens in a new tab)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className="h-3.5 w-3.5 flex-shrink-0"
                >
                  <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                  <circle cx="12" cy="9.5" r="2.5" />
                </svg>
                Get Directions
              </a>
            </span>
            <span>
              <span className="block font-bold text-[#3d428a]">
                Registered Address
              </span>
              <span className="mt-1 block">{COMPANY.registeredAddress}</span>
            </span>
          </address>
        </li>
      </ul>
    </div>
  );
}

function FooterSocial() {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-[#5d69be] sm:text-base">
        Social
      </h3>
      <ul className="flex flex-col gap-2 text-sm text-[#3d428a] sm:text-base">
        <li>
          <a
            href={COMPANY.linkedIn}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 transition-colors hover:text-[#2d3270]"
          >
            {IMAGES.iconLinkedIn ? (
              <img
                src={IMAGES.iconLinkedIn}
                alt=""
                aria-hidden="true"
                className="h-5 w-5 object-contain"
              />
            ) : null}
            LinkedIn
          </a>
        </li>
      </ul>
    </div>
  );
}

function FooterLegal() {
  return (
    <nav aria-label="Legal">
      <ul className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-[#5d69be]/80 sm:justify-end sm:text-sm">
        {LEGAL_LINKS.map((link, index) => (
          <li key={link.to} className="flex items-center gap-x-2">
            {index > 0 && <span aria-hidden="true">-</span>}
            <Link
              to={link.to}
              className="whitespace-nowrap transition-colors hover:text-[#2d3270]"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Footer() {
  const currentYear = new Date().getFullYear();
  const copyrightYear =
    COMPANY.year === currentYear
      ? currentYear
      : `${COMPANY.year}-${currentYear}`;

  return (
    <footer
      className="w-full border-t border-[#5d69be]/20 bg-[#e4e7f2] text-[#3d428a]"
      role="contentinfo"
      aria-label="Footer"
    >
      <div className="mx-auto max-w-[1800px] px-4 py-12 sm:px-6 md:py-16 lg:px-10 xl:px-14 2xl:px-20">
        <StaggerGroup className="flex flex-col gap-10 xl:flex-row xl:items-start xl:gap-8 2xl:gap-10">
          <StaggerItem
            variant="slideUp"
            index={0}
            className="w-full shrink-0 xl:w-1/4"
          >
            <button
              type="button"
              onClick={scrollToTop}
              className="footer-brand-slot block w-full cursor-pointer bg-transparent p-0 text-center focus:outline-none focus:ring-2 focus:ring-[#5d69be] focus:ring-offset-2 focus:ring-offset-[#e4e7f2]"
              aria-label={`${COMPANY.name} - Back to top`}
            >
              <BrandLogo
                loading="lazy"
                size="footer"
                taglineAlign="center"
                className="footer-brand-logo"
              />
            </button>
          </StaggerItem>

          <StaggerItem
            variant="slideUp"
            index={1}
            className="min-w-0 flex-1 xl:w-3/4"
          >
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 xl:grid-cols-[0.8fr_2fr_0.8fr] xl:gap-8">
              <FooterNav />
              <FooterContact />
              <FooterSocial />
            </div>
          </StaggerItem>
        </StaggerGroup>

        <Reveal
          variant="fadeIn"
          index={3}
          className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-[#5d69be]/20 pt-6 text-center text-xs text-[#5d69be]/80 sm:flex-row sm:text-sm"
        >
          <p>
            © {copyrightYear} {COMPANY.name}. All rights reserved.
          </p>
          <FooterLegal />
        </Reveal>
      </div>
    </footer>
  );
}

export default Footer;
